import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, GlobalStyles, Paper, Typography } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import type {
	ColDef,
	ColGroupDef,
	ExcelStyle,
	GridApi,
} from "ag-grid-community";
import { ErrorModal, dxtToLocalServerTime } from "@dexteel/mesf-core";
import moment from "moment";

import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	HEADER_PAD_CLASS,
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import {
	STAND_COLUMNS,
	getStandTooltip,
} from "@/pages/reports/billets-process-data/models/BilletsProcessData";
import { mesfColors } from "@/setup/ag-grid/mesf-theme";
import {
	isUsableDate,
	urlParam,
	useDropUnknownValue,
	useUrlState,
} from "@/shared/hooks/useUrlState";
import {
	useBilletsRFactor,
	useBilletsRFactorPickers,
} from "../hooks/useBilletsRFactor";
import type { BilletRFactorRow } from "../models/BilletsRFactor";
import { BilletsRFactorToolbar } from "./BilletsRFactorToolbar";

const CENTER_HEADER = "ag-cell-center-header";
const HEADER_CLASS = [CENTER_HEADER, HEADER_PAD_CLASS];
const CENTER_CELL = [ONE_LINE_CELL_CLASS];
const DATE_FORMAT = "yyyy-MM-dd HH:mm:ss";

// Excel takes these cells as numbers; the format keeps the decimals the grid shows.
const EXCEL_STYLES: ExcelStyle[] = [
	{ id: "excelInteger", numberFormat: { format: "#,##0" } },
	{ id: "excelRFactor", numberFormat: { format: "0.000" } },
];
const INTEGER_CELL = [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS, "excelInteger"];
const RFACTOR_CELL = [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS, "excelRFactor"];

const NO_ROWS_OVERLAY = `<span style="color:${mesfColors.text.secondary};font-size:13px">No billets were rolled in the selected range</span>`;

const formatNumber = (value: unknown, decimals: number): string =>
	value == null
		? ""
		: Number(value).toLocaleString("en-US", {
				minimumFractionDigits: decimals,
				maximumFractionDigits: decimals,
			});

const formatStart = (start?: string | null): string =>
	start ? dxtToLocalServerTime(start, DATE_FORMAT) : "";

// The fixed columns sit in an empty group so their headers line up with the
// stand columns under the "R-Factor" group.
const inEmptyGroup = (
	col: ColDef<BilletRFactorRow>,
): ColGroupDef<BilletRFactorRow> => ({ headerName: "", children: [col] });

const COLUMN_DEFS: (
	| ColDef<BilletRFactorRow>
	| ColGroupDef<BilletRFactorRow>
)[] = [
	inEmptyGroup({
		colId: "Start",
		field: "Start",
		headerName: "Start",
		width: 150,
		sortable: true,
		sort: "desc",
		cellClass: CENTER_CELL,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatStart(p.value),
	}),
	inEmptyGroup({
		field: "ProductName",
		headerName: "Product",
		flex: 3,
		minWidth: 160,
		sortable: true,
		cellClass: CENTER_CELL,
		headerClass: HEADER_CLASS,
		tooltipField: "ProductName",
	}),
	inEmptyGroup({
		field: "BilletSize",
		headerName: "Size",
		width: 64,
		sortable: true,
		cellClass: CENTER_CELL,
		headerClass: HEADER_CLASS,
	}),
	inEmptyGroup({
		field: "Length",
		headerName: "Length",
		width: 70,
		sortable: true,
		cellClass: INTEGER_CELL,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 0),
	}),
	inEmptyGroup({
		field: "BilletWeight",
		headerName: "Billet Weight",
		width: 96,
		sortable: true,
		cellClass: INTEGER_CELL,
		headerClass: HEADER_CLASS,
		valueFormatter: (p) => formatNumber(p.value, 0),
	}),
	inEmptyGroup({
		field: "CrewId",
		headerName: "Shift",
		width: 56,
		sortable: true,
		cellClass: CENTER_CELL,
		headerClass: HEADER_CLASS,
	}),
	{
		headerName: "R-Factor",
		headerClass: HEADER_CLASS,
		marryChildren: true,
		children: STAND_COLUMNS.map<ColDef<BilletRFactorRow>>((stand) => ({
			field: stand,
			headerName: stand,
			headerTooltip: getStandTooltip(stand),
			flex: 1,
			minWidth: 52,
			sortable: false,
			cellClass: RFACTOR_CELL,
			headerClass: HEADER_CLASS,
			valueFormatter: (p) => formatNumber(p.value, 3),
		})),
	},
];

const URL_SCHEMA = {
	start: urlParam.date(() => moment().startOf("day")),
	end: urlParam.date(() => moment().startOf("day")),
	productTypeId: urlParam.int(),
	productSizeId: urlParam.int(),
	crewId: urlParam.string(),
	billetSizeId: urlParam.int(),
};

export const BilletsRFactorReport = () => {
	const [
		{ start, end, productTypeId, productSizeId, crewId, billetSizeId },
		setFilters,
		{ resetValues, dropValues, isDefault },
	] = useUrlState(URL_SCHEMA);
	const [errorMsg, setErrorMsg] = useState("");
	const gridApiRef = useRef<GridApi<BilletRFactorRow> | null>(null);

	const startStr = start.format("YYYY-MM-DD");
	const endStr = end.format("YYYY-MM-DD");

	const pickersQuery = useBilletsRFactorPickers({
		start: startStr,
		end: endStr,
	});
	const dataQuery = useBilletsRFactor({
		start: startStr,
		end: endStr,
		billetSizeId,
		crewId,
		productSizeId,
		productTypeId,
	});

	const pickers = pickersQuery.data;
	const productSizes = useMemo(() => {
		const all = pickers?.productSizes ?? [];
		return productTypeId == null
			? all
			: all.filter((size) => size.ProductTypeId === productTypeId);
	}, [pickers, productTypeId]);

	// A value the range no longer offers would keep filtering the grid while its
	// control reads as if nothing were selected.
	const pickersLoaded = pickersQuery.isSuccess && !pickersQuery.isPreviousData;
	useDropUnknownValue(
		productTypeId,
		pickersLoaded,
		(pickers?.productTypes ?? []).map((type) => type.Id),
		useCallback(() => dropValues("productTypeId"), [dropValues]),
	);
	useDropUnknownValue(
		productSizeId,
		pickersLoaded,
		productSizes.map((size) => size.Id),
		useCallback(() => dropValues("productSizeId"), [dropValues]),
	);
	useDropUnknownValue(
		crewId,
		pickersLoaded,
		(pickers?.crews ?? []).map((crew) => crew.Id),
		useCallback(() => dropValues("crewId"), [dropValues]),
	);
	useDropUnknownValue(
		billetSizeId,
		pickersLoaded,
		(pickers?.billetSizes ?? []).map((size) => size.Id),
		useCallback(() => dropValues("billetSizeId"), [dropValues]),
	);

	useEffect(() => {
		const error = (dataQuery.error ?? pickersQuery.error) as Error | null;
		if (error) setErrorMsg(error.message);
	}, [dataQuery.error, pickersQuery.error]);

	const rows = dataQuery.data ?? [];

	const handleExportToExcel = useCallback(() => {
		gridApiRef.current?.exportDataAsExcel({
			fileName: `Billets_RFactor_${startStr}_${endStr}.xlsx`,
			sheetName: "Billets R-Factor",
			processCellCallback: (params) =>
				params.column.getColId() === "Start"
					? formatStart(params.value)
					: (params.value ?? ""),
		});
	}, [startStr, endStr]);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "calc(100vh - 64px)",
				gap: 1,
				p: 1,
				pb: 2,
				bgcolor: "#f0f2f5",
			}}
		>
			<Typography variant="h5" sx={{ fontWeight: 600 }}>
				Billets R-Factor
			</Typography>
			<OneLineCellStyles />
			<GlobalStyles
				styles={{
					".ag-cell-center-header .ag-header-cell-label": {
						justifyContent: "center",
					},
				}}
			/>
			<Paper elevation={3}>
				<BilletsRFactorToolbar
					start={start}
					end={end}
					productTypeId={productTypeId}
					productSizeId={productSizeId}
					crewId={crewId}
					billetSizeId={billetSizeId}
					productTypes={pickers?.productTypes ?? []}
					productSizes={productSizes}
					crews={pickers?.crews ?? []}
					billetSizes={pickers?.billetSizes ?? []}
					onStartChange={(d) => isUsableDate(d) && setFilters({ start: d })}
					onEndChange={(d) => isUsableDate(d) && setFilters({ end: d })}
					onProductTypeChange={(id) => {
						const sizeStillFits =
							productSizeId == null ||
							(pickers?.productSizes ?? []).some(
								(size) =>
									size.Id === productSizeId &&
									(id == null || size.ProductTypeId === id),
							);
						setFilters({
							productTypeId: id,
							productSizeId: sizeStillFits ? productSizeId : null,
						});
					}}
					onProductSizeChange={(id) => setFilters({ productSizeId: id })}
					onCrewChange={(crew) => setFilters({ crewId: crew })}
					onBilletSizeChange={(id) => setFilters({ billetSizeId: id })}
					onReset={() => resetValues()}
					canReset={!isDefault()}
					onExportExcel={handleExportToExcel}
					canExport={rows.length > 0}
				/>
			</Paper>

			<Paper elevation={3} sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
				{dataQuery.isLoading ? (
					<CenteredLazyLoading />
				) : (
					<AgGridReact<BilletRFactorRow>
						columnDefs={COLUMN_DEFS}
						rowData={rows}
						getRowId={(p) => String(p.data.Id)}
						defaultColDef={{ resizable: true }}
						excelStyles={EXCEL_STYLES}
						overlayNoRowsTemplate={NO_ROWS_OVERLAY}
						enableBrowserTooltips
						suppressContextMenu
						suppressCellFocus
						headerHeight={32}
						groupHeaderHeight={32}
						rowHeight={28}
						onGridReady={(e) => {
							gridApiRef.current = e.api;
						}}
					/>
				)}
			</Paper>

			<ErrorModal error={errorMsg} onHide={() => setErrorMsg("")} />
		</Box>
	);
};
